import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import api from '../../api/client';
import { useMarkCategoryRead } from '../../hooks/useMarkCategoryRead';

interface TodoBoard {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string;
  board_type: 'personal' | 'family';
  item_count: number;
  completed_count: number;
}

interface CreateBoardForm {
  name: string;
  description: string;
  color: string;
}

export default function TodoBoards() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentFamily } = useFamilyStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Mark todo notifications as read when visiting this page
  useMarkCategoryRead('todos');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateBoardForm>({
    defaultValues: { color: '#6366f1' }
  });

  const { data: boards, isLoading } = useQuery({
    queryKey: ['todoBoards', currentFamily?.id],
    queryFn: async () => {
      if (!currentFamily?.id) return [];
      const response = await api.get(`/todos/family/${currentFamily.id}/boards`);
      return response.data as TodoBoard[];
    },
    enabled: !!currentFamily?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateBoardForm) => {
      if (!currentFamily?.id) throw new Error('No family selected');
      const response = await api.post(`/todos/family/${currentFamily.id}/boards`, {
        ...data,
        board_type: 'family',
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['todoBoards'] });
      toast.success('Board created!');
      setShowCreateModal(false);
      reset();
      navigate(`/todos/${data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create board');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (boardId: string) => {
      await api.delete(`/todos/boards/${boardId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoBoards'] });
      toast.success('Board deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete board');
    },
  });

  if (!currentFamily) {
    return (
      <div className="page-content">
        <div className="card text-center py-16">
          <ClipboardDocumentListIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Family Selected</h2>
          <p className="text-surface-400 mb-6">Select or create a family to manage todo boards</p>
          <Link to="/families" className="btn-primary inline-flex">
            Go to Families
          </Link>
        </div>
      </div>
    );
  }

  const colors = [
    '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e',
    '#f97316', '#eab308', '#22c55e', '#14b8a6', '#0ea5e9',
  ];

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Todo Boards</h1>
          <p className="text-surface-400">{currentFamily.name}</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
          <PlusIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">New Board</span>
        </button>
      </div>

      {/* Boards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : boards?.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <ClipboardDocumentListIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Boards Yet</h2>
          <p className="text-surface-400 mb-6">Create your first todo board</p>
          <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors">
            <PlusIcon className="w-3.5 h-3.5" />
            <span>Create Board</span>
          </button>
        </motion.div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards?.map((board, index) => (
            <motion.div
              key={board.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="relative"
            >
              <Link
                to={`/todos/${board.id}`}
                className="card-interactive block"
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${board.color}20` }}
                  >
                    <ClipboardDocumentListIcon
                      className="w-6 h-6"
                      style={{ color: board.color }}
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (window.confirm('Delete this board?')) {
                        deleteMutation.mutate(board.id);
                      }
                    }}
                    className="btn-icon text-surface-500 hover:text-red-400"
                    title="Delete board"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{board.name}</h3>
                {board.description && (
                  <p className="text-sm text-surface-400 mb-3 line-clamp-2">
                    {board.description}
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm text-surface-400">
                  <span>{board.item_count} items</span>
                  <span>{board.completed_count} done</span>
                </div>
                {board.item_count > 0 && (
                  <div className="mt-3 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(board.completed_count / board.item_count) * 100}%`,
                        backgroundColor: board.color,
                      }}
                    />
                  </div>
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowCreateModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Create Board</h2>
                <button onClick={() => setShowCreateModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Board Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Household Tasks"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-400">{errors.name.message}</p>
                  )}
                </div>

                <div className="input-group">
                  <label className="input-label">Description</label>
                  <textarea
                    className="input resize-none"
                    rows={2}
                    placeholder="What's this board for?"
                    {...register('description')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {colors.map((color) => (
                      <label key={color} className="cursor-pointer">
                        <input
                          type="radio"
                          value={color}
                          className="sr-only peer"
                          {...register('color')}
                        />
                        <div
                          className="w-8 h-8 rounded-lg border-2 border-transparent peer-checked:border-white transition-all"
                          style={{ backgroundColor: color }}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="btn-primary flex-1"
                  >
                    {createMutation.isPending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

